import PropTypes from "prop-types";
import {
  Copy, Eye, MoreHorizontal, Pencil, Plug, Star, Trash2, X,
} from "lucide-react";
import { ChannelIcon } from "@/components/common/ChannelIcon";
import { BrandChannelMark, hasBrandChannelMark } from "@/components/common/BrandChannelMark";
import { cn } from "@/lib/utils";
import { campaignStatusDisplayLabel, isWizardInProgress } from "@/lib/campaignWorkflow";
import {
  ACTIVE_SIGNAL_SOURCES,
  campaignHasRunningSignals,
  isActiveSignalSource,
} from "@/lib/campaignSignals";
import { displayCampaignName } from "@/lib/campaignName";
import {
  formatRelativeUpdated,
} from "./campaignDashboardUtils";

const CHANNEL_META = {
  email: { label: "Email", abbr: "Em", color: "#DC2626" },
  gmail: { label: "Gmail", abbr: "Gm", color: "#EA4335" },
  linkedin: { label: "LinkedIn", abbr: "in", color: "#0A66C2" },
  whatsapp: { label: "WhatsApp", abbr: "Wa", color: "#16A34A" },
  sms: { label: "SMS", abbr: "SMS", color: "#7C3AED" },
  telegram: { label: "Telegram", abbr: "Tg", color: "#0891B2" },
  voice: { label: "Voice", abbr: "Vo", color: "#E11D48" },
  rnc: { label: "RNC", abbr: "RNC", color: "#4F46E5" },
  custom: { label: "ABM List", abbr: "ABM", color: "#0F766E" },
};

export function channelMeta(key) {
  return CHANNEL_META[key] || {
    label: String(key || "Channel"),
    abbr: String(key || "?").slice(0, 2).toUpperCase(),
    color: "#6366F1",
  };
}

function OutreachChannelBadge({ channelKey, size = 24 }) {
  const meta = channelMeta(channelKey);
  if (hasBrandChannelMark(channelKey)) {
    return (
      <span title={meta.label} aria-label={meta.label} style={{ display: "inline-flex", flexShrink: 0 }}>
        <BrandChannelMark channelKey={channelKey} size={size} />
      </span>
    );
  }
  return (
    <span
      title={meta.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.22),
        background: `${meta.color}1A`,
        color: meta.color,
        flexShrink: 0,
      }}
      aria-label={meta.label}
    >
      <ChannelIcon channel={channelKey} size={Math.round(size * 0.45)} />
    </span>
  );
}

OutreachChannelBadge.propTypes = {
  channelKey: PropTypes.string.isRequired,
  size: PropTypes.number,
};

function assignedSignalSources(campaign) {
  const fromSignals = Array.isArray(campaign?.signalSources) ? campaign.signalSources : [];
  if (fromSignals.length) {
    return fromSignals.filter((key) => isActiveSignalSource(key));
  }
  const signals = campaign?.assignedSignals || [];
  return signals.filter((key) => isActiveSignalSource(key));
}

/** Declarative overflow menu: in-progress drafts get delete only; finished campaigns get full menu + delete. */
function campaignCardMenuActions({ inProgress, isArchived, canAssign, canUnassign, isDefault }) {
  const fullMenu = !inProgress;
  return [
    {
      id: isDefault ? "clear-default" : "set-default",
      label: isDefault ? "Clear Default Campaign" : "Set as Default Campaign",
      icon: Star,
      show: fullMenu && !isArchived,
    },
    { id: "assign", label: "Assign Signals", icon: Plug, show: fullMenu && canAssign },
    { id: "unassign", label: "Unassign Signals", icon: X, show: fullMenu && canUnassign },
    { id: "sep-workflow", type: "separator", show: fullMenu },
    { id: "duplicate", label: "Duplicate Workflow", icon: Copy, show: fullMenu },
    { id: "edit", label: "Edit Workflow", icon: Pencil, show: fullMenu },
    { id: "view", label: "View Workflow", icon: Eye, show: fullMenu },
    { id: "sep-lifecycle", type: "separator", show: fullMenu },
    { id: "delete", label: "Delete Workflow", icon: Trash2, danger: true, show: true },
  ].filter((item) => item.show);
}

function statusBadgeClass(status, { inProgress = false } = {}) {
  if (inProgress) return "amber";
  if (status === "active") return "green";
  if (status === "paused") return "amber";
  if (status === "draft") return "blue";
  if (status === "archived") return "gray";
  return "gray";
}

export function CampaignCard({
  campaign,
  onOpen,
  onView,
  onDuplicate,
  onDelete,
  onSetDefault,
  onClearDefault,
  onAssignChannels,
  onUnassignChannels,
  menuOpen = false,
  onMenuOpenChange,
  busy = false,
}) {
  const signals = assignedSignalSources(campaign);
  const signalsLocked = campaignHasRunningSignals(campaign);
  const canAssign = !signalsLocked && signals.length < ACTIVE_SIGNAL_SOURCES.size;
  const canUnassign = !signalsLocked && signals.length > 0;
  const conversion = Number(campaign.conversionRate || 0);
  const hasConversion = conversion > 0 || campaign.qualifiedRNCCount > 0;
  const inProgress = isWizardInProgress(campaign);
  const displayStatus = campaignStatusDisplayLabel(campaign);
  const isArchived = campaign.status === "archived";
  const isDefault = Boolean(campaign.isDefault);

  const menuActionHandlers = {
    "set-default": onSetDefault,
    "clear-default": onClearDefault,
    assign: onAssignChannels,
    unassign: onUnassignChannels,
    duplicate: onDuplicate,
    edit: onOpen,
    view: onView || onOpen,
    delete: onDelete,
  };
  const menuItems = campaignCardMenuActions({
    inProgress,
    isArchived,
    canAssign,
    canUnassign,
    isDefault,
  });

  const closeMenu = () => onMenuOpenChange(false);
  const run = (fn) => () => {
    closeMenu();
    fn(campaign);
  };

  let conversionText = "—";
  if (hasConversion) {
    const fractionDigits = conversion % 1 ? 1 : 0;
    conversionText = `${conversion.toFixed(fractionDigits)}%`;
  }

  const openCampaign = () => {
    if (busy) return;
    if (inProgress) onOpen?.(campaign);
    else onView?.(campaign);
  };

  return (
    <article
      className={cn("ccard", isDefault && "is-default", busy && "opacity-60 pointer-events-none")}
      data-testid={`campaign-card-${campaign.id}`}
      data-default-campaign={isDefault ? "true" : "false"}
      style={busy ? { opacity: 0.6, pointerEvents: "none", position: "relative" } : { position: "relative" }}
    >
      <button
        type="button"
        aria-label={`Open ${campaign.campaignName}`}
        onClick={openCampaign}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "transparent", border: 0, cursor: "pointer", zIndex: 0 }}
      />
      <div className="chead">
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h4 className="campaign-card-title min-w-0" title={campaign.campaignName}>
              {displayCampaignName(campaign.campaignName)}
            </h4>
            {isDefault && (
              <span
                className="badge sm indigo shrink-0"
                data-testid={`campaign-default-badge-${campaign.id}`}
                style={{ position: "relative", zIndex: 1 }}
              >
                <Star size={11} fill="currentColor" aria-hidden />
                Default Campaign
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="menu-dots"
          aria-label={`Actions for ${campaign.campaignName}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          data-testid={`campaign-menu-${campaign.id}`}
          style={{ position: "relative", zIndex: 1 }}
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpenChange(!menuOpen);
          }}
        >
          <MoreHorizontal size={17} />
        </button>
      </div>

      <div className="ch-row" aria-label="Assigned signals">
        {signals.length === 0 ? (
          <span style={{ fontSize: 12, color: "var(--slate-400)" }}>No signals</span>
        ) : (
          <>
            {signals.map((ch) => (
              <OutreachChannelBadge key={ch} channelKey={ch} size={24} />
            ))}
            <span style={{ fontSize: 12, color: "var(--slate-500)", marginLeft: 4 }}>
              {signals.map((ch) => channelMeta(ch).label).join(", ")}
            </span>
          </>
        )}
      </div>

      <div className="stats3">
        <div>
          <div className="lbl">Running Signals</div>
          <div className="val">{campaign.runningSignalsCount}</div>
        </div>
        <div>
          <div className="lbl">Qualified</div>
          <div className="val pos">{campaign.qualifiedRNCCount}</div>
        </div>
        <div>
          <div className="lbl">Conversion</div>
          <div className="val pos">
            {conversionText}
          </div>
        </div>
      </div>

      <div className="foot">
        <span className={`badge sm ${statusBadgeClass(campaign.status, { inProgress })}`}>
          <span className="dotb" />
          {displayStatus}
        </span>
        <span style={{ fontSize: 12, color: "var(--slate-400)" }}>
          {formatRelativeUpdated(campaign.updatedAt, campaign.lastActivity || "—")}
        </span>
      </div>

      {menuOpen && (
        <div
          className="dropdown"
          role="menu"
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={() => {}}
        >
          {menuItems.map((item) => {
            if (item.type === "separator") {
              return <div key={item.id} className="dropdown-sep" />;
            }
            const Icon = item.icon;
            const handler = menuActionHandlers[item.id];
            return (
              <button
                key={item.id}
                type="button"
                className={cn("dropdown-item", item.danger && "danger")}
                role="menuitem"
                onClick={run(handler)}
              >
                <Icon size={14} /> {item.label}
              </button>
            );
          })}
        </div>
      )}
    </article>
  );
}

CampaignCard.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.string.isRequired,
    campaignName: PropTypes.string.isRequired,
    owner: PropTypes.string,
    assignedChannels: PropTypes.arrayOf(PropTypes.string),
    channels: PropTypes.arrayOf(PropTypes.string),
    signalSources: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.string,
    runningSignalsCount: PropTypes.number,
    qualifiedRNCCount: PropTypes.number,
    conversionRate: PropTypes.number,
    updatedAt: PropTypes.string,
    lastActivity: PropTypes.string,
    isDefault: PropTypes.bool,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  onClearDefault: PropTypes.func.isRequired,
  onAssignChannels: PropTypes.func.isRequired,
  onUnassignChannels: PropTypes.func.isRequired,
  menuOpen: PropTypes.bool,
  onMenuOpenChange: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
